import React, { useState } from 'react';
import { FestivalData, ImageData, Map } from '../../../../model/FestivalData';
import ImageEditor from '../ImageEditor/ImageEditor';
import MapOverlay from '../MapOverlay/MapOverlay';
import { getImageUrl } from '../../../../services/imageService';
import Modal from '../../../../components/Modal';
import { useDialogs } from '../../../../components/Dialogs';
import { useMessages } from '../../../../i18n/I18n';
import PageHeader from '../../../../components/PageHeader';
import { FiImage, FiPlus } from 'react-icons/fi';
import { v4 as uuidv4 } from 'uuid';
import './MapsEditor.css';

interface MapsEditorProps {
  maps: Map[];
  onChange: (newMaps: Map[]) => void;
  getApiImageUrl: (identifier: string) => string;
}

const DEFAULT_COORDINATES = { // Defaulting to France being fully visible
  top_left: { latitude: 51.107866, longitude: -7.817688 },
  bottom_right: { latitude: 41.642034, longitude: 11.797337 }
};

const MapsEditor: React.FC<MapsEditorProps> = ({ maps, onChange, getApiImageUrl }) => {
  const [editingMap, setEditingMap] = useState<Map | null>(null);
  const [tempMap, setTempMap] = useState<Map | null>(null);
  const [isEditingCoordinates, setIsEditingCoordinates] = useState(false);
  const { confirm, alert } = useDialogs();
  const t = useMessages('maps');
  const common = useMessages('common');

  const handleAddMap = () => {
    const newMap: Map = {
      identifier: uuidv4(),
      name: t.newMap,
      image: null,
      bounds: {
        top_left: { latitude: 0, longitude: 0 },
        bottom_right: { latitude: 0, longitude: 0 }
      }
    };
    setTempMap(newMap);
    setEditingMap(newMap);
  };

  const handleEditMap = (map: Map) => {
    setTempMap({ ...map });
    setEditingMap(map);
  };

  const handleRemoveMap = async (identifier: string) => {
    if (await confirm(t.deleteConfirm, { title: t.deleteTitle, confirmLabel: common.delete, danger: true })) {
      onChange(maps.filter(map => map.identifier !== identifier));
      setEditingMap(null);
      setTempMap(null);
    }
  };

  const handleSaveMap = () => {
    if (!tempMap) return;
    
    // Validate map has image and bounds set
    if (!tempMap.image) {
      alert(t.imageMissing, { title: t.cantSave });
      return;
    }

    const hasValidBounds = 
      tempMap.bounds.top_left.latitude !== 0 ||
      tempMap.bounds.top_left.longitude !== 0 ||
      tempMap.bounds.bottom_right.latitude !== 0 ||
      tempMap.bounds.bottom_right.longitude !== 0;

    if (!hasValidBounds) {
      alert(t.coordinatesMissing, { title: t.cantSave });
      return;
    }

    // Check if we're editing an existing map or adding a new one
    const isNewMap = !maps.some(map => map.identifier === tempMap.identifier);
    
    let updatedMaps: Map[];
    if (isNewMap) {
      updatedMaps = [...maps, tempMap];
    } else {
      updatedMaps = maps.map(map => 
        map.identifier === tempMap.identifier ? tempMap : map
      );
    }
    
    onChange(updatedMaps);
    setEditingMap(null);
    setTempMap(null);
    setIsEditingCoordinates(false);
  };

  const handleImageChange = (imageData: ImageData | undefined) => {
    if (!tempMap) return;
    setTempMap({
      ...tempMap,
      image: imageData || null
    });
  };

  const handleMapBoundsChanged = (bounds: {
    north: number;
    south: number;
    east: number;
    west: number;
  }) => {
    if (!tempMap) return;
    
    const newBounds = {
      top_left: {
        latitude: bounds.north,
        longitude: bounds.west
      },
      bottom_right: {
        latitude: bounds.south,
        longitude: bounds.east
      }
    };

    setTempMap({
      ...tempMap,
      bounds: newBounds
    });
  };

  const handleEditCoordinates = () => {
    if (!tempMap) return;

    // Check if coordinates are at default values (0,0)
    const hasDefaultCoordinates = 
      tempMap.bounds.top_left.latitude == 0 &&
      tempMap.bounds.top_left.longitude == 0 &&
      tempMap.bounds.bottom_right.latitude == 0 &&
      tempMap.bounds.bottom_right.longitude == 0;

    if (hasDefaultCoordinates) {
      setTempMap({
        ...tempMap,
        bounds: DEFAULT_COORDINATES
      });
    }

    setIsEditingCoordinates(true);
  };

  const renderModalContent = () => {
    if (!tempMap) return null;

    if (isEditingCoordinates && tempMap.image) {
      return (
        <div className="coordinates-edit-view">
          <h3>{t.coordinatesTitle}</h3>
          <div className="coordinates-map-container">
            <MapOverlay
              imageUrl={getImageUrl(tempMap.image, getApiImageUrl)!}
              onBoundsChanged={handleMapBoundsChanged}
              initialBounds={tempMap.bounds}
            />
          </div>
          <div className="modal-actions">
            <button 
              onClick={handleSaveMap}
              className="save-button"
              disabled={!tempMap.image || !tempMap.name}
            >
              {common.save}
            </button>
            <button 
              onClick={() => setIsEditingCoordinates(false)}
              className="cancel-button"
            >
              {common.back}
            </button>
            {editingMap?.identifier && (
              <button 
                onClick={() => handleRemoveMap(editingMap.identifier)}
                className="delete-button"
              >
                {common.delete}
              </button>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="map-edit-content">
        <h3>{editingMap?.identifier ? t.editTitle : t.addTitle}</h3>
        
        <div className="form-field">
          <label>{t.name}</label>
          <input
            type="text"
            value={tempMap.name}
            onChange={(e) => setTempMap({
              ...tempMap,
              name: e.target.value
            })}
            placeholder={t.namePlaceholder}
          />
        </div>

        <div className="form-field">
          <label>{t.image}</label>
          <ImageEditor
            fillMode='contain'
            currentImage={tempMap.image || undefined}
            onChange={handleImageChange}
            getApiImageUrl={getApiImageUrl}
          />
        </div>

        {tempMap.image && (
          <div className="map-coordinates-section">
            <div className="coordinates-info">
              <span>{t.currentCoordinates}</span>
              <div className="coordinates-values">
                <span>
                  {t.topLeft} ({tempMap.bounds.top_left.latitude}, 
                  {tempMap.bounds.top_left.longitude})
                  <br />
                  {t.bottomRight} ({tempMap.bounds.bottom_right.latitude}, 
                  {tempMap.bounds.bottom_right.longitude})
                </span>
              </div>
            </div>
            <button 
              className="coordinates-button"
              onClick={handleEditCoordinates}
            >
              {t.editCoordinates}
            </button>
          </div>
        )}

        <div className="modal-actions">
          <button 
            onClick={handleSaveMap}
            className="save-button"
            disabled={!tempMap.image || !tempMap.name}
          >
            {common.save}
          </button>
          <button 
            onClick={() => {
              setEditingMap(null);
              setTempMap(null);
              setIsEditingCoordinates(false);
            }}
            className="cancel-button"
          >
            {common.cancel}
          </button>
          {editingMap?.identifier && (
            <button 
              onClick={() => handleRemoveMap(editingMap.identifier)}
              className="delete-button"
            >
              {common.delete}
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="maps-editor">
      <PageHeader
        title={t.title}
        count={maps.length}
        description={t.subtitle}
      >
        <button type="button" onClick={handleAddMap} className="btn btn-primary">
          <FiPlus aria-hidden="true" />
          {t.addMap}
        </button>
      </PageHeader>

      <div className="maps-grid">
        {maps.map(map => (
          <div key={map.identifier} className="map-card" onClick={() => handleEditMap(map)}>
            <div className="map-image">
              {map.image
                ? <img src={getImageUrl(map.image, getApiImageUrl)} alt={map.name} />
                : <span className="map-image-missing" aria-hidden="true"><FiImage /></span>}
            </div>
            <div className="map-info">
              <h3>{map.name || t.untitled}</h3>
              <span className="coordinates-preview" title={t.boundsHint}>
                {map.bounds.top_left.latitude}, {map.bounds.top_left.longitude}
                {' → '}
                {map.bounds.bottom_right.latitude}, {map.bounds.bottom_right.longitude}
              </span>
            </div>
          </div>
        ))}
        {maps.length === 0 && (
          <div className="empty-state">{t.empty}</div>
        )}
      </div>

      <Modal
        isOpen={editingMap !== null}
        onClose={() => {
          setEditingMap(null);
          setTempMap(null);
          setIsEditingCoordinates(false);
        }}
        className={`map-edit-modal ${isEditingCoordinates ? 'coordinates-mode' : ''}`}
      >
        {renderModalContent()}
      </Modal>
    </div>
  );
};

export default MapsEditor; 